import { DataTableColumn } from "mantine-datatable";
import { useMemo } from "react";

import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import Spoiler from "../../../components/Spoiler/Spoiler.tsx";
import { TableData, TableRow } from "../../../components/Table/types.ts";
import TableFilesDisplay from "../../../components/TableFilesDisplay/TableFilesDisplay.tsx";
import {
  LimitRequest,
  toHumanReadableLimitRequestStatus,
} from "../../../types/limitRequest/limitRequest.ts";
import { toLocalDate } from "../../../utils/date.ts";
import { toCurrencyPrice } from "../../../utils/number.ts";
import { toHumanReadable } from "../../../utils/string.ts";
import { getUserDisplayName } from "../../../utils/user.ts";

interface Props {
  data: LimitRequest[] | null | undefined;
}

export const useSubmissionListTableData = ({ data }: Props): TableData => {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "id",
      hidden: true,
    },
    {
      accessor: "companyName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Customer",
            fieldName: "name",
          }}
        >
          Customer Name
        </DatabaseMetadataPopover>
      ),
      width: 300,
      sortable: true,
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "createdAt",
          }}
        >
          CLIK Request Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitRequestType",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitRequestType",
          }}
        >
          Limit Request Type
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitName",
          }}
        >
          Limit Name
        </DatabaseMetadataPopover>
      ),
      width: 300,
      sortable: true,
    },
    {
      accessor: "requestStatus",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "requestStatus",
          }}
        >
          Current Process
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "cuAssigned.name",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "cuAssigned",
          }}
        >
          CU Assigned
        </DatabaseMetadataPopover>
      ),
      width: 200,
      sortable: true,
    },
    {
      accessor: "creditUnderwriting.notes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditUnderwriting",
            fieldName: "notes",
          }}
        >
          CU Notes
        </DatabaseMetadataPopover>
      ),
      width: 300,
    },
    {
      accessor: "creditUnderwriting.analysisFile",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditUnderwriting",
            fieldName: "analysisFile",
          }}
        >
          CU Analysis
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "creditUnderwriting.analysisResultDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditUnderwriting",
            fieldName: "analysisResultDate",
          }}
        >
          CU Analysis Result Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "creditUnderwriting.visitDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditUnderwriting",
            fieldName: "visitDate",
          }}
        >
          CU Visit Date
        </DatabaseMetadataPopover>
      ),
      width: 150,
      sortable: true,
    },
    {
      accessor: "rrAssignedDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "rrAssignedDate",
          }}
        >
          Handover to RR Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "riskReview.analysisResultDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "RiskReview",
            fieldName: "analysisResultDate",
          }}
        >
          RR analysis result date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "riskReview.analysisResult",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "RiskReview",
            fieldName: "analysisResult",
          }}
        >
          RR analysis result
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "riskReview.notes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "RiskReview",
            fieldName: "notes",
          }}
        >
          RR Notes
        </DatabaseMetadataPopover>
      ),
      width: 300,
      sortable: true,
    },
    {
      accessor: "riskReview.document",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "RiskReview",
            fieldName: "document",
          }}
        >
          RR Document
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "creditApproval.date",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "CreditApproval",
            fieldName: "date",
          }}
        >
          CC Date
        </DatabaseMetadataPopover>
      ),
      width: 150,
      sortable: true,
    },
    {
      accessor: "limitApproved",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "limitApproved",
          }}
        >
          Limit Approved
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "tenorApproved",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "LimitRequest",
            fieldName: "tenorApproved",
          }}
        >
          Tenor Approved
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
  ];

  const rows = useMemo(() => {
    return data?.map((item) => {
      return {
        id: item.id,
        companyName: item.company.name,
        createdAt: toLocalDate(item.createdAt),
        limitRequestType: toHumanReadable(item.limitRequestType),
        limitName: item.limitName,
        requestStatus:
          item.requestStatus &&
          toHumanReadableLimitRequestStatus(item.requestStatus),
        "cuAssigned.name":
          item?.cuAssigned && getUserDisplayName(item.cuAssigned),
        "creditUnderwriting.notes": item.creditUnderwriting?.notes && (
          <Spoiler maxStringLength={300} text={item.creditUnderwriting.notes} />
        ),
        "creditUnderwriting.analysisResultDate": toLocalDate(
          item.creditUnderwriting?.analysisResultDate
        ),
        "creditUnderwriting.analysisFile": item.creditUnderwriting
          ?.analysisFile && (
          <TableFilesDisplay files={[item.creditUnderwriting.analysisFile]} />
        ),
        "creditUnderwriting.visitDate": toLocalDate(
          item.creditUnderwriting?.visitDate
        ),
        rrAssignedDate: toLocalDate(item.rrAssignedDate),
        "riskReview.analysisResultDate": toLocalDate(
          item.riskReview?.analysisResultDate
        ),
        "riskReview.analysisResult": toHumanReadable(
          item.riskReview?.analysisResult
        ),
        "riskReview.document": item.riskReview?.document && (
          <TableFilesDisplay files={[item.riskReview.document]} />
        ),
        "riskReview.notes": item.riskReview?.notes && (
          <Spoiler maxStringLength={300} text={item.riskReview.notes} />
        ),
        "creditApproval.date": toLocalDate(item.creditApproval?.date),
        limitApproved: toCurrencyPrice(item.limitApproved),
        tenorApproved: item.tenorApproved,
      };
    });
  }, [data]);

  return [columns, rows];
};
